import { KeyRound } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { ApiError, type AccountApi } from "@hostdock/sdk";
import { Button } from "../button";
import { FormError } from "../feedback";
import { Field, Input } from "../form";

export function LoginScreen<TSession>({ api, onAuthenticated, title = "Sign in" }: { api: AccountApi<TSession>; onAuthenticated(session: TSession): void; title?: string }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);

  const login = useMutation({ meta: { inlineError: true },
    mutationFn: () => api.login({ email, password }),
    onSuccess: (res) => {
      if (res.status === "MFA_REQUIRED") setChallenge(res.challengeId);
      else onAuthenticated(res.session);
    },
  });
  const verify = useMutation({
    mutationFn: () => api.loginMfa(challenge!, code.trim()),
    onSuccess: (res) => {
      if (res.status === "AUTHENTICATED") onAuthenticated(res.session);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "MFA_CHALLENGE_EXPIRED") {
        setChallenge(null);
        setCode("");
      }
    },
  });

  if (challenge) {
    const submit = (e: FormEvent) => {
      e.preventDefault();
      verify.mutate();
    };
    return (
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <div>
          <h1 className="text-[17px] font-semibold">Two-factor verification</h1>
          <p className="mt-1 text-[13px] text-muted">
            {useRecovery ? "Enter one of your saved recovery codes." : "Enter the 6-digit code from your authenticator app."}
          </p>
        </div>
        <Field label={useRecovery ? "Recovery code" : "Authentication code"}>
          {(p) => (
            <Input
              {...p}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode={useRecovery ? "text" : "numeric"}
              autoComplete="one-time-code"
              placeholder={useRecovery ? "XXXXX-XXXXX" : "123456"}
              autoFocus
              className="font-mono tracking-widest"
            />
          )}
        </Field>
        <FormError error={verify.error} />
        <Button type="submit" variant="primary" loading={verify.isPending} disabled={!code.trim()}>
          Verify
        </Button>
        <div className="flex justify-between text-[12.5px]">
          <button type="button" className="text-accent hover:underline" onClick={() => { setUseRecovery(!useRecovery); setCode(""); }}>
            {useRecovery ? "Use authenticator app" : "Use a recovery code"}
          </button>
          <button type="button" className="text-muted hover:text-fg" onClick={() => { setChallenge(null); setCode(""); verify.reset(); }}>
            Start over
          </button>
        </div>
      </form>
    );
  }

  const submit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate();
  };
  const fieldErrors = login.error instanceof ApiError ? login.error.fieldErrors : {};
  return (
    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
      <div>
        <h1 className="text-[17px] font-semibold">{title}</h1>
        <p className="mt-1 text-[13px] text-muted">Use your HostDock account credentials.</p>
      </div>
      <Field label="Email" error={fieldErrors.email}>
        {(p) => <Input {...p} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" autoFocus required />}
      </Field>
      <Field label="Password" error={fieldErrors.password}>
        {(p) => <Input {...p} type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />}
      </Field>
      {login.error && !Object.keys(fieldErrors).length ? <FormError error={login.error} /> : null}
      <Button type="submit" variant="primary" icon={<KeyRound size={15} />} loading={login.isPending} disabled={!email || !password}>
        Sign in
      </Button>
    </form>
  );
}
